/** Browser-owned editor model for the existing catalog DraftContent wire schema. No evidence/scan authority. */
export type CatalogDraft = {
  sourceLocale?: string;
  variants: { id: string; sku: string; options: Record<string, string>; price?: {currency:string;minor:string} }[];
  media: { id: string; version: number; alt: string }[];
  taxonomy: string[];
  translations: { locale: string; title: string; description: string; attribution?: "original" | "human-translated" | "machine-assisted";
    mediaAlt?: {id:string;alt:string}[];
    attributes?: {variantId:string;options:{key:string;label:string;value:string}[]}[];
  }[];
};
export function catalogDraft(value: unknown): CatalogDraft | undefined {
  if (!value || typeof value !== "object") return;
  const v = value as CatalogDraft;
  // Optional metadata must not dereference malformed response rows.
  if (Array.isArray(v.translations)) {
    if (v.translations.length > 6) return;
    for (const row of v.translations)
      if (!row || typeof row !== "object" || Array.isArray(row)) return;
  }
  if (Object.hasOwn(v,"sourceLocale") && (typeof v.sourceLocale!=="string" ||
      !Array.isArray(v.translations) || !v.translations.some(t=>t.locale===v.sourceLocale))) return;
  if (Array.isArray(v.translations) && v.translations.some(t=>!t || Object.keys(t).some(k=>!["locale","title","description","attribution","mediaAlt","attributes","state"].includes(k)) ||
    Object.hasOwn(t,"attribution") && (typeof t.attribution!=="string" || !["original","human-translated","machine-assisted"].includes(t.attribution) ||
     !v.sourceLocale || (t.locale===v.sourceLocale ? t.attribution!=="original" : t.attribution==="original")))) return;
  if (![v.variants, v.media, v.taxonomy, v.translations].every(Array.isArray))
    return;
  if (
    v.variants.length > 200 ||
    v.media.length > 30 ||
    v.translations.length > 6 ||
    v.taxonomy.length > 1000 ||
    !v.variants.every(
      (row) =>
        row &&
        typeof row.id === "string" &&
        typeof row.sku === "string" &&
        (!Object.hasOwn(row,"price") || !!row.price && typeof row.price === "object" && Object.keys(row.price).sort().join() === "currency,minor" && typeof row.price.currency === "string" && typeof row.price.minor === "string") &&
        row.options &&
        typeof row.options === "object" &&
        !Array.isArray(row.options) &&
        Object.values(row.options).every((x) => typeof x === "string"),
    ) ||
    !v.media.every(
      (row) =>
        row &&
        typeof row.id === "string" &&
        Number.isSafeInteger(row.version) &&
        typeof row.alt === "string",
    ) ||
    !v.translations.every(
      (row) =>
        row &&
        typeof row.locale === "string" &&
        typeof row.title === "string" &&
        typeof row.description === "string",
    ) ||
    !v.taxonomy.every((x) => typeof x === "string")
  )
    return;
  if (v.translations.some(row => !localizedShape(v, row))) return;
  return structuredClone({
    variants: v.variants,
    media: v.media,
    taxonomy: v.taxonomy,
    translations: v.translations.map((t) => ({
      locale: t.locale,
      title: t.title,
      description: t.description,
      ...(Object.hasOwn(t,"attribution") ? {attribution:t.attribution} : {}),
      ...(Object.hasOwn(t,"mediaAlt") ? {mediaAlt:t.mediaAlt!.map(m=>({id:m.id,alt:m.alt}))} : {}),
      ...(Object.hasOwn(t,"attributes") ? {attributes:t.attributes!.map(g=>({variantId:g.variantId,options:g.options.map(o=>({key:o.key,label:o.label,value:o.value}))}))} : {}),
    })),
    ...(Object.hasOwn(v,"sourceLocale") ? {sourceLocale:v.sourceLocale} : {}),
  });
}
function localizedShape(value: CatalogDraft, row: CatalogDraft["translations"][number]): boolean {
  const record = (v:unknown, keys:string[]):v is Record<string,any> =>
    !!v && typeof v==="object" && !Array.isArray(v) &&
    Object.keys(v).length===keys.length && keys.every(k=>Object.hasOwn(v,k));
  const unique = (v:string[]) => new Set(v).size===v.length;
  if (Object.hasOwn(row,"mediaAlt")) {
    if (!Array.isArray(row.mediaAlt) || !row.mediaAlt.length || row.mediaAlt.length>30 ||
        Array.from(row.mediaAlt).some(m=>!record(m,["id","alt"]) || typeof m.id!=="string" ||
          typeof m.alt!=="string" || !value.media.some(source=>source.id===m.id)) ||
        !unique(row.mediaAlt.map(m=>m.id))) return false;
  }
  if (Object.hasOwn(row,"attributes")) {
    if (!Array.isArray(row.attributes) || !row.attributes.length || row.attributes.length>200 ||
        Array.from(row.attributes).some(group=>{
          if (!record(group,["variantId","options"]) || typeof group.variantId!=="string") return true;
          const variant=value.variants.find(v=>v.id===group.variantId);
          return !variant || !Array.isArray(group.options) || !group.options.length || group.options.length>10 ||
            Array.from(group.options).some((option:unknown)=>!record(option,["key","label","value"]) ||
              typeof option.key!=="string" || !Object.hasOwn(variant.options,option.key) ||
              typeof option.label!=="string" || typeof option.value!=="string") ||
            !unique(group.options.map((o:{key:string})=>o.key));
        }) || !unique(row.attributes.map(v=>v.variantId))) return false;
  }
  return true;
}
export function catalogIssues(v: CatalogDraft): string[] {
  const errors: string[] = [];
  const text = (x: unknown) =>
    typeof x === "string" && !!x.trim() && x.length <= 4000;
  const duplicate = (values: string[]) =>
    new Set(values).size !== values.length;
  if (
    !v.variants.length ||
    v.variants.length > 200 ||
    duplicate(v.variants.map((x) => x.id)) ||
    duplicate(v.variants.map((x) => x.sku.trim().toLowerCase()))
  )
    errors.push("variants");
  v.variants.forEach((row, i) => {
    if (
      !text(row.id) ||
      !text(row.sku) ||
      Object.hasOwn(row,"price") && (!row.price || !/^[A-Z]{3}$/.test(row.price.currency) || !/^[1-9][0-9]*$/.test(row.price.minor) || Object.keys(row.price).sort().join()!=="currency,minor") ||
      !row.options ||
      Object.keys(row.options).length > 10 ||
      Object.entries(row.options).some(
        ([k, val]) =>
          !text(k) ||
          !text(val) ||
          [
            "__proto__",
            "constructor",
            "prototype",
            "actor",
            "role",
            "tenant",
          ].includes(k),
      )
    )
      errors.push(`variants-${i}`);
  });
  if (v.media.length > 30 || duplicate(v.media.map((x) => x.id)))
    errors.push("media");
  v.media.forEach((row, i) => {
    if (
      !text(row.id) ||
      !Number.isSafeInteger(row.version) ||
      row.version < 1 ||
      !text(row.alt)
    )
      errors.push(`media-${i}`);
  });
  if (
    !v.taxonomy.length ||
    v.taxonomy.some((x) => !text(x)) ||
    duplicate(v.taxonomy)
  )
    errors.push("taxonomy");
  if (
    !v.translations.length ||
    v.translations.length > 6 ||
    duplicate(v.translations.map((x) => x.locale))
  )
    errors.push("translations");
  v.translations.forEach((row, i) => {
    if (
      !["en", "es", "fr", "de", "ja", "ar"].includes(row.locale) ||
      !text(row.title) ||
      !text(row.description)
    )
      errors.push(`translations-${i}`);
  });
  if (Object.hasOwn(v,"sourceLocale") && (typeof v.sourceLocale!=="string" ||
      !v.translations.some(t=>t.locale===v.sourceLocale))) errors.push("sourceLocale");
  v.translations.forEach((t,i)=>{if(Object.hasOwn(t,"attribution") &&
    (typeof t.attribution!=="string" || !["original","human-translated","machine-assisted"].includes(t.attribution) ||
     !v.sourceLocale || (t.locale===v.sourceLocale ? t.attribution!=="original" : t.attribution==="original")))
    errors.push("attribution-"+i)});
  v.translations.forEach((row,i)=>{
    if (!localizedShape(v,row) ||
        row.mediaAlt?.some(m=>!text(m.alt)) ||
        row.attributes?.some(group=>group.options.some(o=>!text(o.label)||!text(o.value))))
      errors.push("localized-fields-"+i);
  });
  return errors;
}
export function moveRow<T>(rows: T[], from: number, to: number): T[] {
  const next = rows.slice();
  if (from < 0 || from >= next.length || to < 0 || to >= next.length)
    return next;
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}
